import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import PackageCard from '../components/PackageCard';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import { PACKAGES, MONTHLY_PLANS } from '../data/content';
import { ShieldCheck } from 'lucide-react';

const EXCLUSIONS = [
  'Domain registration (typically a small annual fee, in your name)',
  'Hosting and website platform subscriptions',
  'Premium plugins, themes or software licences',
  'Paid advertising (Google Ads, Facebook Ads and so on)',
  'Third-party subscriptions',
  'Copywriting or photography beyond the agreed scope',
  'Additional pages or work outside your package — always quoted first',
];

export default function PricingPage() {
  return (
    <>
      <Seo
        title="Pricing & Packages — Fixed Prices for Trade Websites | Build My Trade"
        description="One-off website and online presence packages from £249, plus optional monthly support from £39. Fixed prices in GBP, everything listed, nothing hidden."
        path="/pricing"
      />
      <PageHero
        eyebrow="Pricing"
        title="Fixed prices. Everything listed."
        intro="Three one-off packages to get your trade business online properly, and three optional monthly plans if you want ongoing help. Prices are in GBP and cover exactly the deliverables shown."
        crumbs={[{ label: 'Pricing' }]}
        testid="pricing-heading"
      />

      {/* One-off packages */}
      <section className="bg-offwhite" data-testid="oneoff-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="One-off packages"
            title="Get set up properly, once"
            intro="Each package builds on the one before it — you’re never charged twice for the same deliverable."
            testid="oneoff-heading"
          />
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex items-start gap-4 rounded-2xl border border-hairline bg-white p-6">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-lime">
                <ShieldCheck className="h-5 w-5 text-charcoal" aria-hidden="true" />
              </span>
              <p className="text-sm leading-relaxed text-stonebrand" data-testid="no-surprise-note">
                <span className="font-semibold text-charcoal">No surprise charges.</span> The price you see is the
                price for the deliverables listed. If anything extra is ever needed, you get a clear quote first —
                and the choice is always yours.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Monthly plans */}
      <section className="border-t border-charcoal-line bg-charcoal" data-testid="monthly-plans">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <SectionHeading
            dark
            eyebrow="Optional monthly support"
            title="Want it looked after? Add a monthly plan."
            intro="Completely optional — your one-off package works perfectly well on its own. Monthly plans are rolling: cancel before your next billing date and nothing more is taken."
            testid="monthly-heading"
          />
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {MONTHLY_PLANS.map((plan, i) => (
              <PackageCard key={plan.slug} pkg={plan} delay={0.08 * i} />
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-xs leading-relaxed text-offwhite/50">
              On content plans, each post is created once and adapted for both Facebook and Instagram. Video filming,
              paid advertising, photography and replying to customer messages are not included unless separately
              agreed in writing.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Exclusions */}
      <section className="bg-offwhite" data-testid="pricing-exclusions">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Full transparency"
                title="What’s not included"
                intro="Unless your written quote expressly says otherwise, these are paid separately — usually by you directly, so the accounts stay in your name."
                testid="exclusions-heading"
              />
              <Reveal delay={0.15}>
                <ArrowLink to="/contact" variant="dark" testid="pricing-quote-cta" className="mt-8">
                  Get Your Free Quote
                </ArrowLink>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid gap-3 sm:grid-cols-2">
                {EXCLUSIONS.map((item, i) => (
                  <Reveal key={i} delay={0.05 * i}>
                    <li className="h-full rounded-xl border border-hairline bg-white p-5 text-sm leading-relaxed text-charcoal/85">
                      {item}
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-charcoal-line bg-charcoal-deep bg-blueprint" data-testid="pricing-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Not sure which package <span className="text-lime">fits?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Choose “Not sure — please advise” on the quote form and tell us where your business is today. We’ll
                recommend the smallest package that genuinely does the job.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact?package=not-sure" variant="lime" testid="pricing-cta-quote" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
